// ════════════════════════════════════════════════════════════════════
// Glass-i — Sales Tax Invoice list (Phase 1B).
// Professional AntD Table with status tabs + filters, "New Tax Invoice" and
// "Import from Proforma" actions, a draft-discard action, and a per-invoice
// activity drawer. No Post / Finalize action (Phase 2).
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Input, Tag, Space, DatePicker, Select, Segmented, Drawer,
  Modal, message, Card, Tooltip
} from 'antd';
import { PlusOutlined, ImportOutlined, ReloadOutlined, HistoryOutlined, DeleteOutlined, AuditOutlined, StopOutlined } from '@ant-design/icons';
import useAuth from '../../../../hooks/useAuth';
import { hasPermission } from '../proforma/proformaHelpers';
import { invoiceApi } from './invoiceApi';
import ImportProformaModal from './ImportProformaModal';
import InvoiceEventsTimeline from './InvoiceEventsTimeline';
import CancelPostedInvoiceModal from './CancelPostedInvoiceModal';
import {
  INVOICE_STATUS_TABS, buildInvoiceListParams, invoiceStatusColor, invoiceStatusLabel,
  gstTypeLabel, releaseStatusTag, eInvoiceStatusLabel, sourceLabel, formatMoney, postErrorMessage, cancelErrorMessage
} from './invoiceHelpers';

const { RangePicker } = DatePicker;

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const perms = me && me.permissions;
  const canCreate = hasPermission(perms, 'erp.sales.invoice.create');
  const canImport = hasPermission(perms, 'erp.sales.invoice.import');
  const canDiscard = hasPermission(perms, 'erp.sales.invoice.discard');
  const canPost = hasPermission(perms, 'erp.sales.invoice.post');
  const canCancel = hasPermission(perms, 'erp.sales.invoice.cancel');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState('all');
  const [search, setSearch] = useState('');
  const [gstType, setGstType] = useState(null);
  const [source, setSource] = useState(null);
  const [range, setRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [importOpen, setImportOpen] = useState(false);
  const [historyId, setHistoryId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildInvoiceListParams({
        status: statusTab,
        search: search || undefined,
        gstType: gstType || undefined,
        source: source || undefined,
        dateFrom: range && range[0] ? range[0].format('YYYY-MM-DD') : undefined,
        dateTo: range && range[1] ? range[1].format('YYYY-MM-DD') : undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize
      });
      const data = await invoiceApi.list(params);
      setRows((data && data.items) || []);
      setTotal((data && data.total) || 0);
    } catch (err) {
      message.error(backendError(err, 'Failed to load tax invoices.'));
    } finally {
      setLoading(false);
    }
  }, [statusTab, search, gstType, source, range, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const doDiscard = useCallback((record) => {
    Modal.confirm({
      title: 'Discard this Tax Invoice draft?',
      content: 'This draft will be closed and cannot be edited again. If it was imported from a Proforma, that Proforma will become available for invoicing again.',
      okText: 'Discard draft', okType: 'danger', cancelText: 'Keep',
      onOk: async () => {
        try {
          await invoiceApi.discard(record.id);
          message.success('Draft discarded.');
          load();
        } catch (err) {
          message.error(backendError(err, 'Could not discard the draft.'));
        }
      }
    });
  }, [load]);

  // Post a DRAFT to the ledger (Phase 2). Backend is authoritative; the async
  // onOk keeps the OK button loading and blocks a double-click. On failure the
  // modal stays open and the backend error CODE is mapped to a clear message.
  const doPost = useCallback((record) => {
    const gst = Number(record.cgstAmount || 0) + Number(record.sgstAmount || 0) + Number(record.igstAmount || 0);
    Modal.confirm({
      title: `Post Tax Invoice ${record.invoiceNo || ''}?`.trim(),
      okText: 'Post Invoice', cancelText: 'Cancel', width: 460,
      content: (
        <div className="text-xs text-slate-600 leading-relaxed">
          <div className="mb-2 text-slate-500">
            Posting will create the accounting entry and lock this Tax Invoice from further editing.
            Any later correction must follow the applicable cancellation, credit-note or debit-note workflow.
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 tabular-nums">
            <span className="text-slate-500">Customer</span><span>{record.customerName || '—'}</span>
            <span className="text-slate-500">Taxable Value</span><span>{formatMoney(record.taxableAmount)}</span>
            <span className="text-slate-500">GST</span><span>{formatMoney(record.gstAmount != null ? record.gstAmount : gst)}</span>
            <span className="text-slate-500">Round Off</span><span>{formatMoney(record.roundOffAmount)}</span>
            <span className="text-slate-500 font-semibold">Grand Total</span><span className="font-semibold">{formatMoney(record.grandTotal)}</span>
          </div>
        </div>
      ),
      onOk: async () => {
        try {
          await invoiceApi.post(record.id);
          message.success('Tax Invoice posted successfully. Accounting voucher created.');
          load();
        } catch (err) {
          const code = err && err.response && err.response.data && err.response.data.error && err.response.data.error.code;
          message.error(postErrorMessage(code, backendError(err, 'Could not post the Tax Invoice.')));
          throw err; // keep the modal open
        }
      }
    });
  }, [load]);

  // Cancel a POSTED invoice — reverses the accounting voucher (Phase 2C). The
  // shared modal captures the mandatory reason; on success we reload the list.
  const doCancelConfirm = useCallback(async (reason) => {
    if (!cancelTarget) return;
    try {
      await invoiceApi.cancel(cancelTarget.id, reason);
      message.success('Tax Invoice cancelled. The accounting voucher was reversed.');
      setCancelTarget(null);
      load();
    } catch (err) {
      const code = err && err.response && err.response.data && err.response.data.error && err.response.data.error.code;
      message.error(cancelErrorMessage(code, backendError(err, 'Could not cancel the Tax Invoice.')));
      // keep the modal open on failure
    }
  }, [cancelTarget, load]);

  const columns = useMemo(() => [
    { title: 'Invoice No', dataIndex: 'invoiceNo', key: 'invoiceNo', width: 150, fixed: 'left',
      render: (v, r) => <a className="font-medium tabular-nums" onClick={() => navigate(`/erp/sales/invoices/${r.id}`)}>{v || '—'}</a> },
    { title: 'Date', dataIndex: 'invoiceDate', key: 'invoiceDate', width: 105 },
    { title: 'Customer', dataIndex: 'customerName', key: 'customerName', ellipsis: true, render: (v) => v || '—' },
    { title: 'Project', dataIndex: 'projectName', key: 'projectName', width: 130, ellipsis: true, render: (v) => v || '—' },
    { title: 'Source', dataIndex: 'sourceType', key: 'sourceType', width: 120, render: (v) => <Tag>{sourceLabel(v)}</Tag> },
    { title: 'GST Type', dataIndex: 'gstInvoiceType', key: 'gstInvoiceType', width: 100, render: (v) => gstTypeLabel(v) },
    { title: 'Taxable', dataIndex: 'taxableAmount', key: 'taxableAmount', width: 130, align: 'right',
      render: (v) => <span className="tabular-nums">{formatMoney(v)}</span> },
    { title: 'GST', dataIndex: 'gstAmount', key: 'gstAmount', width: 120, align: 'right',
      render: (v) => <span className="tabular-nums">{formatMoney(v)}</span> },
    { title: 'Grand Total', dataIndex: 'grandTotal', key: 'grandTotal', width: 140, align: 'right',
      render: (v) => <span className="font-semibold tabular-nums">{formatMoney(v)}</span> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v) => <Tag color={invoiceStatusColor(v)}>{invoiceStatusLabel(v)}</Tag> },
    { title: 'Release', dataIndex: 'releaseStatus', key: 'releaseStatus', width: 120,
      render: (v) => { const t = releaseStatusTag(v); return <Tag color={t.color}>{t.label}</Tag>; } },
    { title: 'E-Invoice', dataIndex: 'eInvoiceStatus', key: 'eInvoiceStatus', width: 110, render: (v) => eInvoiceStatusLabel(v) },
    { title: 'Voucher', dataIndex: 'postedVoucherNo', key: 'postedVoucherNo', width: 130,
      render: (v) => (v ? <span className="tabular-nums">{v}</span> : '—') },
    { title: 'Updated', dataIndex: 'updatedAt', key: 'updatedAt', width: 150,
      render: (v) => (v ? new Date(v).toLocaleString('en-IN') : '—') },
    { title: '', key: 'actions', width: 160, fixed: 'right',
      render: (_v, r) => {
        const s = String(r.status).toLowerCase();
        const isDraft = s === 'draft';
        const isPosted = s === 'posted';
        return (
          <Space size={4}>
            <Tooltip title="Activity">
              <Button size="small" type="text" icon={<HistoryOutlined />} onClick={() => setHistoryId(r.id)} />
            </Tooltip>
            {canPost && isDraft ? (
              <Tooltip title="Post Tax Invoice">
                <Button size="small" type="text" icon={<AuditOutlined />} onClick={() => doPost(r)} />
              </Tooltip>
            ) : null}
            {canDiscard && isDraft ? (
              <Tooltip title="Discard draft">
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => doDiscard(r)} />
              </Tooltip>
            ) : null}
            {canCancel && isPosted ? (
              <Tooltip title="Cancel Posted Invoice">
                <Button size="small" type="text" danger icon={<StopOutlined />} onClick={() => setCancelTarget(r)} />
              </Tooltip>
            ) : null}
          </Space>
        );
      } }
  ], [navigate, canDiscard, doDiscard, canPost, doPost, canCancel]);

  return (
    <div className="flex flex-col gap-3">
      <Card
        size="small"
        styles={{ body: { padding: 12 } }}
        title={<span className="text-base font-semibold">Tax Invoices</span>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
            {canImport ? <Button icon={<ImportOutlined />} onClick={() => setImportOpen(true)}>Import from Proforma</Button> : null}
            {canCreate ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/erp/sales/invoices/new')}>
                New Tax Invoice
              </Button>
            ) : null}
          </Space>
        }
      >
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Segmented
            value={statusTab}
            onChange={(v) => { setStatusTab(v); setPage(1); }}
            options={INVOICE_STATUS_TABS.map((t) => ({ label: t.label, value: t.key }))}
          />
          <Input.Search
            placeholder="Invoice number or customer"
            allowClear
            style={{ width: 260 }}
            onSearch={(v) => { setSearch(v); setPage(1); }}
            onChange={(e) => { if (!e.target.value) { setSearch(''); } }}
          />
          <Select
            placeholder="GST type" allowClear style={{ width: 130 }} value={gstType || undefined}
            onChange={(v) => { setGstType(v || null); setPage(1); }}
            options={['b2b', 'b2cl', 'b2cs', 'export', 'sez'].map((v) => ({ value: v, label: gstTypeLabel(v) }))}
          />
          <Select
            placeholder="Source" allowClear style={{ width: 150 }} value={source || undefined}
            onChange={(v) => { setSource(v || null); setPage(1); }}
            options={[{ value: 'manual', label: 'Manual' }, { value: 'proforma_import', label: 'From Proforma' }]}
          />
          <RangePicker onChange={(v) => { setRange(v); setPage(1); }} />
        </div>

        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1500 }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showTotal: (t) => `${t} invoice${t === 1 ? '' : 's'}`
          }}
        />
      </Card>

      <ImportProformaModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(created) => { setImportOpen(false); if (created && created.id) navigate(`/erp/sales/invoices/${created.id}`); }}
      />

      <Drawer
        title="Invoice activity"
        width={460}
        open={!!historyId}
        onClose={() => setHistoryId(null)}
        destroyOnClose
      >
        {historyId ? <InvoiceEventsTimeline invoiceId={historyId} /> : null}
      </Drawer>

      <CancelPostedInvoiceModal
        open={!!cancelTarget}
        invoice={cancelTarget ? {
          invoiceNo: cancelTarget.invoiceNo, customerName: cancelTarget.customerName,
          grandTotal: cancelTarget.grandTotal, postedVoucherNo: cancelTarget.postedVoucherNo
        } : {}}
        onClose={() => setCancelTarget(null)}
        onConfirm={doCancelConfirm}
      />
    </div>
  );
}
